/**
 * Authentication hook for managing user state.
 * Provides current user, profile, and loading state.
 */

import { useState, useEffect, createContext, useContext, ReactNode, useCallback, useMemo } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { Profile } from "@/lib/supabase-helpers";

// ============================================
// Context type definition
// ============================================

interface AuthContextType {
  /** Current authenticated user (null if not logged in) */
  user: User | null;
  /** Current user's profile data (null if not logged in) */
  profile: Profile | null;
  /** Current session */
  session: Session | null;
  /** Whether auth state is still loading */
  loading: boolean;
  /** Whether the current user is an admin */
  isAdmin: boolean;
  /** Refresh the profile data */
  refreshProfile: () => Promise<void>;
  /** Check if user has admin role (forces refresh) */
  checkAdminStatus: () => Promise<boolean>;
}

// Create context with default values
const AuthContext = createContext<AuthContextType>({
  user: null,
  profile: null,
  session: null,
  loading: true,
  isAdmin: false,
  refreshProfile: async () => {},
  checkAdminStatus: async () => false,
});

// ============================================
// Auth Provider Component
// ============================================

interface AuthProviderProps {
  children: ReactNode;
}

/**
 * Provider component that wraps the app and provides auth state.
 * Must be placed near the root of the component tree.
 */
export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  // SECURITY NOTE: isAdmin state is for UI/UX only (showing/hiding admin features).
  // Actual security is enforced by RLS policies on the database using is_admin() function.
  // Never trust client-side isAdmin for security decisions - RLS is the authority.
  const [isAdmin, setIsAdmin] = useState(false);

  /**
   * Check if user has admin role via secure RPC function.
   * SECURITY: This calls a SECURITY DEFINER function on the server.
   * The result is used for UI purposes only - RLS enforces actual access control.
   */
  const checkAdminStatus = useCallback(async (): Promise<boolean> => {
    // Must have authenticated user
    if (!user?.id) {
      setIsAdmin(false);
      return false;
    }

    try {
      // SECURITY: Use getUser() to get fresh user data from server, not cached session
      const { data: authData, error: authError } = await supabase.auth.getUser();
      if (authError || !authData.user) {
        console.error("Auth verification failed:", authError);
        setIsAdmin(false);
        return false;
      }

      // SECURITY: Verify the user ID matches what we expect
      if (authData.user.id !== user.id) {
        console.error("User ID mismatch - potential session tampering");
        setIsAdmin(false);
        return false;
      }

      // Check admin status via secure server-side function
      const { data, error } = await supabase.rpc('is_admin', { p_user_id: authData.user.id });
      if (error) {
        console.error("Error checking admin status:", error);
        setIsAdmin(false);
        return false;
      }
      const adminStatus = !!data;
      setIsAdmin(adminStatus);
      return adminStatus;
    } catch (error) {
      console.error("Error checking admin status:", error);
      setIsAdmin(false);
      return false;
    }
  }, [user?.id]);

  /**
   * Fetch the user's profile from the database.
   * Safe to call from UI; avoids calling auth.getUser() when we already have user id.
   */
  const refreshProfile = useCallback(async () => {
    try {
      if (!user?.id) {
        setProfile(null);
        return;
      }

      // ✅ Explicit column select (audit cleanup)
      const { data, error } = await supabase
        .from("profiles")
        .select("id, email, full_name, role, school_id, school_name, theme_preset, teacher_referral, avatar_url, created_at")
        .eq("id", user.id)
        .maybeSingle();

      if (error) throw error;
      setProfile(data as Profile | null);
    } catch (error) {
      console.error("Error fetching profile:", error);
      setProfile(null);
    }
  }, [user?.id]);

  useEffect(() => {
    let isMounted = true;
    let authChangeHandled = false;

    const fetchProfileById = async (userId: string): Promise<Profile | null> => {
      // ✅ Explicit column select (audit cleanup)
      const { data, error } = await supabase
        .from("profiles")
        .select("id, email, full_name, role, school_id, school_name, theme_preset, teacher_referral, avatar_url, created_at")
        .eq("id", userId)
        .maybeSingle();

      if (error) throw error;
      return data as Profile | null;
    };

    const loadProfileAndFinish = async (userId: string | undefined) => {
      try {
        if (userId) {
          const profileData = await fetchProfileById(userId);
          if (isMounted) setProfile(profileData);
        } else {
          if (isMounted) setProfile(null);
        }
      } catch (error) {
        console.error("Error fetching profile:", error);
        if (isMounted) setProfile(null);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    // onAuthStateChange is the primary source of truth
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, newSession) => {
      if (!isMounted) return;
      authChangeHandled = true;

      setSession(newSession);

      if (event === "SIGNED_IN" || event === "SIGNED_OUT") {
        setUser(newSession?.user ?? null);
        void loadProfileAndFinish(newSession?.user?.id);
      } else if (event === "TOKEN_REFRESHED") {
        setUser((prev) => {
          if (prev?.id === newSession?.user?.id) return prev;
          return newSession?.user ?? null;
        });
      } else if (event === "INITIAL_SESSION") {
        setUser(newSession?.user ?? null);
        void loadProfileAndFinish(newSession?.user?.id);
      }
    });

    // getSession is a fallback — only use if onAuthStateChange hasn't fired yet
    supabase.auth.getSession().then(({ data: { session: existingSession } }) => {
      if (!isMounted) return;
      if (authChangeHandled) return;

      // If there's a valid session, wait for onAuthStateChange to handle it
      if (existingSession) return;

      // No session and no auth change yet — give onAuthStateChange a chance
      setTimeout(() => {
        if (!isMounted || authChangeHandled) return;
        setSession(null);
        setUser(null);
        void loadProfileAndFinish(undefined);
      }, 100);
    });

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, []);

  // Memoized so consumers only re-render when auth state actually changes.
  const value = useMemo(
    () => ({ user, profile, session, loading, isAdmin, refreshProfile, checkAdminStatus }),
    [user, profile, session, loading, isAdmin, refreshProfile, checkAdminStatus],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// ============================================
// Hook to use auth context
// ============================================

/**
 * Hook to access authentication state and profile.
 * Must be used within an AuthProvider.
 */
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
